import {
  mdiAccountGroup,
  mdiAutoFix,
  mdiBookshelf,
  mdiCalendarStar,
  mdiCardAccountDetailsOutline,
  mdiCards,
  mdiChartBar,
  mdiCogs,
  mdiCrownOutline,
  mdiFileDocumentMultiple,
  mdiFilterMultiple,
  mdiFolderMultipleImage,
  mdiGamepadVariant,
  mdiHome,
  mdiNintendoGameBoy,
  mdiPacMan,
  mdiStorefrontOutline,
  mdiTagMultiple,
  mdiYoutube
} from "@mdi/js"
import type { RouteLocationRaw } from "vue-router"
import auth from "../../auth/keycloak"
import { Role } from "../../graphql/types"
import {
  COMPANIES_STORAGE_KEY,
  GAME_CONSOLES_STORAGE_KEY,
  GAME_SERIES_STORAGE_KEY,
  GAMES_STORAGE_KEY,
  ORDER_STORAGE_KEY,
  SCALE_STORAGE_KEY,
  TAGS_STORAGE_KEY,
  VIEW_STORAGE_KEY
} from "../../media/media"
import {
  ARTICLE_AUTHORS_STORAGE_KEY,
  ARTICLE_TAGS_STORAGE_KEY,
  ARTICLE_COMPANIES_STORAGE_KEY,
  ARTICLE_GAME_CONSOLES_STORAGE_KEY,
  ARTICLE_GAME_SERIES_STORAGE_KEY,
  ARTICLE_GAMES_STORAGE_KEY,
  ARTICLE_KINDS_STORAGE_KEY,
  ARTICLE_PUBLISHED_STATES_STORAGE_KEY,
  ARTICLE_CAROUSEL_PLACEMENTS_STORAGE_KEY,
  ARTICLE_PUBLICATION_YEAR_STORAGE_KEY,
  ARTICLE_PUBLICATION_MONTH_STORAGE_KEY,
  ARTICLE_ORDER_STORAGE_KEY,
  ARTICLE_WITHOUT_STORAGE_KEY,
  ARTICLE_WITH_ANY_STORAGE_KEY,
} from "../../articles/articles"
import {
  GAME_GENRES_STORAGE_KEY,
  GAME_GAME_SERIES_STORAGE_KEY,
  GAME_GAME_CONSOLES_STORAGE_KEY,
  GAME_COMPANIES_STORAGE_KEY,
  GAME_GAME_MODES_STORAGE_KEY,
  GAME_USK_STORAGE_KEY,
  GAME_PEGI_STORAGE_KEY,
  GAME_ESRB_STORAGE_KEY,
  GAME_RELEASE_YEAR_STORAGE_KEY,
  GAME_RELEASE_MONTH_STORAGE_KEY,
  GAME_ORDER_STORAGE_KEY,
} from "../../games/games"
import {
  AUTHOR_ACTIVE_STATES_STORAGE_KEY,
  AUTHOR_PROFILE_VISIBILITIES_STORAGE_KEY,
  AUTHOR_LINKED_USER_SUBS_STORAGE_KEY,
  AUTHOR_LINKED_USERS_INCLUDE_NONE_STORAGE_KEY,
  AUTHOR_ORDER_STORAGE_KEY,
} from "../../author/authors"
import {
  USER_CLASSIFICATIONS_STORAGE_KEY,
  USER_HAS_LINKED_AUTHOR_STORAGE_KEY,
  USER_ORDER_STORAGE_KEY,
  USER_ROLES_STORAGE_KEY,
  USER_STATES_STORAGE_KEY,
} from "../../users/users"
import { getStoredBooleanValue, getStoredValue, getStoredValues } from "../../storage"

export interface NavigationItem {
  name: string
  routeName: string
  to: RouteLocationRaw
  icon: string
}

/** Route names that live in the mobile dock (other than the Burger slot). Order matches the dock. */
export const DOCK_ROUTE_NAMES = ["home", "article-list", "community"] as const

/** Same items as DOCK_ROUTE_NAMES, as a Set for O(1) membership checks. */
export const DOCK_ROUTE_NAME_SET: ReadonlySet<string> = new Set(DOCK_ROUTE_NAMES)

export function buildNavigationItems(): NavigationItem[] {
  const items: NavigationItem[] = [
    { name: "Dashboard", routeName: "home", to: { name: "home" }, icon: mdiHome }
  ]

  if ([Role.MANAGE_ARTICLES, Role.MANAGE_OWN_ARTICLES, Role.MANAGE_GAMES, Role.MANAGE_COMPANIES,
       Role.MANAGE_AUTHORS, Role.MANAGE_GAME_CONSOLES, Role.MANAGE_GENRES, Role.MANAGE_GAME_SERIES,
       Role.MANAGE_MEDIA].some((r) => auth.hasRole(r))) {
    items.push({
      name: "Aufbereitung",
      routeName: "aufbereitung-list",
      to: { name: "aufbereitung-list" },
      icon: mdiAutoFix,
    })
  }

  if (auth.hasRole(Role.MANAGE_ARTICLES)) {
    items.push({
      name: "Statistik",
      routeName: "statistik",
      to: { name: "statistik" },
      icon: mdiChartBar,
    })
  }

  if (auth.hasRole(Role.MANAGE_ARTICLES) || auth.hasRole(Role.MANAGE_OWN_ARTICLES)) {
    items.push({
      name: "Beiträge",
      routeName: "article-list",
      to: {
        name: "article-list",
        query: {
          authors:             getStoredValues(ARTICLE_AUTHORS_STORAGE_KEY),
          tags:                getStoredValues(ARTICLE_TAGS_STORAGE_KEY),
          companies:           getStoredValues(ARTICLE_COMPANIES_STORAGE_KEY),
          gameConsoles:        getStoredValues(ARTICLE_GAME_CONSOLES_STORAGE_KEY),
          gameSeries:          getStoredValues(ARTICLE_GAME_SERIES_STORAGE_KEY),
          games:               getStoredValues(ARTICLE_GAMES_STORAGE_KEY),
          without:             getStoredValues(ARTICLE_WITHOUT_STORAGE_KEY),
          withAny:             getStoredValues(ARTICLE_WITH_ANY_STORAGE_KEY),
          kinds:               getStoredValues(ARTICLE_KINDS_STORAGE_KEY),
          publishedStates:     getStoredValues(ARTICLE_PUBLISHED_STATES_STORAGE_KEY),
          carouselPlacements:  getStoredValues(ARTICLE_CAROUSEL_PLACEMENTS_STORAGE_KEY),
          publicationYear:     getStoredValue(ARTICLE_PUBLICATION_YEAR_STORAGE_KEY) || undefined,
          publicationMonth:    getStoredValue(ARTICLE_PUBLICATION_MONTH_STORAGE_KEY) || undefined,
          order:               getStoredValues(ARTICLE_ORDER_STORAGE_KEY, "|"),
        }
      },
      icon: mdiFileDocumentMultiple
    })
  }

  items.push({
    name: "Community",
    routeName: "community",
    to: { name: "community" },
    icon: mdiAccountGroup
  })

  if (auth.hasRole(Role.MANAGE_QUICK_FILTER) || auth.hasRole(Role.MANAGE_OWN_QUICK_FILTER)) {
    items.push({
      name: "Schnellfilter",
      routeName: "quick-filter-list",
      to: { name: "quick-filter-list" },
      icon: mdiFilterMultiple
    })
  }

  if (auth.hasRole(Role.MANAGE_GAMES) || auth.hasRole(Role.MANAGE_OWN_GAMES)) {
    items.push({
      name: "Spiele",
      routeName: "game-list",
      to: {
        name: "game-list",
        query: {
          genres:        getStoredValues(GAME_GENRES_STORAGE_KEY),
          gameSeries:    getStoredValues(GAME_GAME_SERIES_STORAGE_KEY),
          gameConsoles:  getStoredValues(GAME_GAME_CONSOLES_STORAGE_KEY),
          companies:     getStoredValues(GAME_COMPANIES_STORAGE_KEY),
          gameModes:     getStoredValues(GAME_GAME_MODES_STORAGE_KEY),
          usk:           getStoredValues(GAME_USK_STORAGE_KEY),
          pegi:          getStoredValues(GAME_PEGI_STORAGE_KEY),
          esrb:          getStoredValues(GAME_ESRB_STORAGE_KEY),
          releaseYear:   getStoredValue(GAME_RELEASE_YEAR_STORAGE_KEY) || undefined,
          releaseMonth:  getStoredValue(GAME_RELEASE_MONTH_STORAGE_KEY) || undefined,
          order:         getStoredValues(GAME_ORDER_STORAGE_KEY, "|"),
        }
      },
      icon: mdiGamepadVariant
    })
  }

  if (auth.hasRole(Role.MANAGE_GAME_SERIES) || auth.hasRole(Role.MANAGE_OWN_GAME_SERIES)) {
    items.push({
      name: "Spieleserien",
      routeName: "game-series-list",
      to: { name: "game-series-list" },
      icon: mdiCards
    })
  }

  if (auth.hasRole(Role.MANAGE_ARTICLE_SERIES) || auth.hasRole(Role.MANAGE_OWN_ARTICLE_SERIES)) {
    items.push({
      name: "Artikelserien",
      routeName: "article-series-list",
      to: { name: "article-series-list" },
      icon: mdiBookshelf
    })
  }

  if (auth.hasRole(Role.MANAGE_EVENTS) || auth.hasRole(Role.MANAGE_OWN_EVENTS)) {
    items.push({
      name: "Events",
      routeName: "event-list",
      to: { name: "event-list" },
      icon: mdiCalendarStar
    })
  }

  if (auth.hasRole(Role.MANAGE_GENRES) || auth.hasRole(Role.MANAGE_OWN_GENRES)) {
    items.push({
      name: "Genres",
      routeName: "genre-list",
      to: { name: "genre-list" },
      icon: mdiPacMan
    })
  }

  if (auth.hasRole(Role.MANAGE_GAME_CONSOLES) || auth.hasRole(Role.MANAGE_OWN_GAME_CONSOLES)) {
    items.push({
      name: "Spielkonsolen",
      routeName: "game-console-list",
      to: { name: "game-console-list" },
      icon: mdiNintendoGameBoy
    })
  }

  if (auth.hasRole(Role.MANAGE_COMPANIES) || auth.hasRole(Role.MANAGE_OWN_COMPANIES)) {
    items.push({
      name: "Unternehmen",
      routeName: "company-list",
      to: { name: "company-list" },
      icon: mdiStorefrontOutline
    })
  }

  if (auth.hasRole(Role.MANAGE_MEDIA) || auth.hasRole(Role.MANAGE_OWN_MEDIA)) {
    items.push({
      name: "Dateien",
      routeName: "media-list",
      to: {
        name: "media-list",
        query: {
          order: getStoredValues(ORDER_STORAGE_KEY, "|"),
          scale: getStoredValue(SCALE_STORAGE_KEY),
          view: getStoredValue(VIEW_STORAGE_KEY),
          tags: getStoredValues(TAGS_STORAGE_KEY),
          companies: getStoredValues(COMPANIES_STORAGE_KEY),
          gameConsoles: getStoredValues(GAME_CONSOLES_STORAGE_KEY),
          gameSeries: getStoredValues(GAME_SERIES_STORAGE_KEY),
          games: getStoredValues(GAMES_STORAGE_KEY)
        }
      },
      icon: mdiFolderMultipleImage
    })
  }

  if (auth.hasRole(Role.MANAGE_MEDIA)) {
    items.push({
      name: "Videos",
      routeName: "youtube-video-list",
      to: { name: "youtube-video-list" },
      icon: mdiYoutube
    })
  }

  if (auth.hasRole(Role.MANAGE_TAG) || auth.hasRole(Role.MANAGE_OWN_TAG)) {
    items.push({
      name: "Schlagworte / Tags",
      routeName: "tag-list",
      to: { name: "tag-list" },
      icon: mdiTagMultiple
    })
  }

  if (auth.hasRole(Role.MANAGE_RATING_CATEGORIES) || auth.hasRole(Role.MANAGE_OWN_RATING_CATEGORIES)) {
    items.push({
      name: "Bewertungskategorien",
      routeName: "rating-category-list",
      to: { name: "rating-category-list" },
      icon: mdiCrownOutline
    })
  }

  if (auth.hasRole(Role.MANAGE_AUTHORS) || auth.hasRole(Role.MANAGE_OWN_AUTHORS)) {
    items.push({
      name: "Autoren",
      routeName: "author-list",
      to: {
        name: "author-list",
        query: {
          activeStates:           getStoredValues(AUTHOR_ACTIVE_STATES_STORAGE_KEY),
          profileVisibilities:    getStoredValues(AUTHOR_PROFILE_VISIBILITIES_STORAGE_KEY),
          linkedUserSubs:         getStoredValues(AUTHOR_LINKED_USER_SUBS_STORAGE_KEY),
          linkedUsersIncludeNone: getStoredBooleanValue(AUTHOR_LINKED_USERS_INCLUDE_NONE_STORAGE_KEY) ? "1" : undefined,
          order:                  getStoredValues(AUTHOR_ORDER_STORAGE_KEY, "|"),
        }
      },
      icon: mdiCardAccountDetailsOutline
    })
  }

  if (auth.hasRole(Role.MANAGE_USERS)) {
    items.push({
      name: "Benutzer",
      routeName: "user-list",
      to: {
        name: "user-list",
        query: {
          classifications: getStoredValues(USER_CLASSIFICATIONS_STORAGE_KEY),
          roles:           getStoredValues(USER_ROLES_STORAGE_KEY),
          states:          getStoredValues(USER_STATES_STORAGE_KEY),
          hasLinkedAuthor: getStoredValue(USER_HAS_LINKED_AUTHOR_STORAGE_KEY) || undefined,
          order:           getStoredValues(USER_ORDER_STORAGE_KEY, "|"),
        }
      },
      icon: mdiAccountGroup
    })
  }

  if (auth.hasRole(Role.SYSTEM_ADMINISTRATION)) {
    items.push({
      name: "System",
      routeName: "system",
      to: { name: "system" },
      icon: mdiCogs
    })
  }

  return items
}
